/**
 * Smoothing-strength mapping shared by SDF Gaussian smoothing.
 *
 * The implementation is GPU-only and lives in
 * {@link ./GaussianSmoothingGL} (separable X, Y, Z passes); this module
 * only defines the user-facing smoothing strength to Gaussian sigma
 * mapping, so every control resolves its sigma from a single source of
 * truth.
 */

/**
 * Top of the strength range {@link getSigmaForStrength} accepts, and the
 * highest entry of the sigma table below. Exported so UI sliders derive
 * their range from the engine instead of hardcoding a number that could
 * drift from the table.
 */
export const MAX_SMOOTHING_LEVEL = 10;

/**
 * Mapping from strength position (1-{@link MAX_SMOOTHING_LEVEL}) to
 * sigma value. Higher positions produce stronger smoothing; 6-10
 * continue the roughly ×1.3 progression for markedly more aggressive
 * outlines. The constant ratio is what {@link getSigmaForStrength}
 * interpolates BETWEEN the entries, so a stepless control travels at a
 * constant perceived rate too.
 *
 * The top sigma is deliberately 5.0: the base kernel radius is
 * ceil(3σ), so position 10 lands exactly on the GL renderer's shader
 * bound of 15 ({@link ./GaussianSmoothingGL#MAX_KERNEL_RADIUS}, set by
 * the uKernel[32] array and its ±15 loop bound). No position can
 * therefore overflow the kernel on an isotropic grid.
 *
 * The per-axis radius is derived from a per-axis sigma — the physical
 * smoothing extent is held constant in mm, so the Y/Z sigmas are
 * rescaled by resX/resY and resX/resZ. Anisotropy in the usual
 * direction (Z coarser than XY) only shrinks the Z radius. A grid FINER
 * in Y or Z than in X scales the radius up instead and can still exceed
 * 15 at the highest levels; there the GL renderer clamps the radius (it
 * renormalizes the truncated kernel), so the smoothing SATURATES —
 * slightly weaker than requested along that axis — rather than
 * overflowing the kernel array.
 */
const SMOOTHING_LEVEL_TO_SIGMA: Record<number, number> = {
    1: 0.2,
    2: 0.4,
    3: 0.7,
    4: 1.0,
    5: 1.4,
    6: 1.9,
    7: 2.5,
    8: 3.2,
    9: 4.0,
    10: 5.0,
};

/**
 * Sigma for a smoothing-strength position — the module's only mapping,
 * since every control that reaches it presents strength as a stepless
 * slider rather than as ten detents: threshold segmentation
 * (1..{@link MAX_SMOOTHING_LEVEL}) and the structure smoothing panel
 * (1..5, its own narrower travel over the same mapping).
 *
 * The position lives in LEVEL space, 1..{@link MAX_SMOOTHING_LEVEL}, not
 * in sigma space: the table is a ~×1.3 geometric progression, so equal
 * travel in level space is equal PERCEIVED change in smoothing, while a
 * slider linear in sigma would compress the whole light range (0.2..1.0)
 * into the first ~18 % of its travel and spend the rest between 4 and 5.
 *
 * Interpolation between two adjacent table entries is therefore
 * GEOMETRIC — `σ = s[k] · (s[k+1]/s[k])^(p−k)`, `k = floor(p)` — which
 * continues the table's own constant-ratio progression instead of
 * flattening it into a linear ramp between the entries. Two consequences
 * that the tests pin:
 *
 * - every INTEGER position returns exactly the table value, so no preset
 *   default and no previously recorded integer strength changes meaning;
 * - the mapping is strictly increasing, so a position identifies a sigma
 *   and vice versa (a cache key may carry either).
 *
 * DETERMINISM: the UI commits positions QUANTIZED to 0.1 (the slider's
 * step), so the reachable set is the 91 positions 1.0, 1.1, … 10.0 and
 * identical user settings always reproduce an identical sigma — and hence
 * an identical structure. Nothing here rounds the sigma itself; the
 * provenance record is what rounds it for display (2 decimals).
 *
 * @param position Continuous strength position; clamped into
 *        [1, {@link MAX_SMOOTHING_LEVEL}]
 * @returns Sigma value for the Gaussian kernel
 * @throws Error when the position is not a finite number — a NaN would
 *         otherwise propagate into the kernel radius and silently produce
 *         an unsmoothed (or garbage) field rather than failing.
 */
export function getSigmaForStrength(position: number): number {
    if (!Number.isFinite(position)) {
        throw new Error(
            `Smoothing strength must be a finite number, got ${position}`
        );
    }
    const clamped = Math.max(1, Math.min(MAX_SMOOTHING_LEVEL, position));
    const lower = Math.floor(clamped);
    const fraction = clamped - lower;
    // Exact table hit (includes the top of the range, where there is no
    // next entry to interpolate towards).
    if (fraction === 0) {
        return SMOOTHING_LEVEL_TO_SIGMA[lower];
    }
    const sigmaLow = SMOOTHING_LEVEL_TO_SIGMA[lower];
    const sigmaHigh = SMOOTHING_LEVEL_TO_SIGMA[lower + 1];
    return sigmaLow * Math.pow(sigmaHigh / sigmaLow, fraction);
}
